<template>
    <div class="app-layout">
        <!-- If user's agent is Internet Explorer, we show the stub for IE -->
        <IEpage v-if="isIE"></IEpage>

        <VAppLayout v-else-if="isVuexBooted" :isSnowEnabled="isSnowEnabled">
            <Splitpanes class="page" @resized="saveMenuPaneSize">
                <Pane
                    class="page__menu-pane"
                    :size="isAdaptive ? 100 : effectiveMenuPaneSize"
                    max-size="45"
                    :min-size="isAdaptive ? 10 : minMenuPaneSize"
                >
                    <RouterView name="menu"></RouterView>
                </Pane>
                <Pane :size="isAdaptive ? 100 : 100 - effectiveMenuPaneSize">
                    <Splitpanes @resized="saveRequestPaneSize">
                        <Pane :style="{ display: isAdaptive && $route.params.id ? 'none' : 'flex' }">
                            <RouterView></RouterView>
                        </Pane>
                        <RouterView
                            name="sharedPanel"
                            :requestPaneSize="isAdaptive ? 100 : sharedPaneSize"
                        ></RouterView>
                    </Splitpanes>
                </Pane>
            </Splitpanes>
            <VImagePopup></VImagePopup>
            <VErrorModal></VErrorModal>
            <VConfirmModal></VConfirmModal>
            <VRequestPauseModal></VRequestPauseModal>
            <VRequestChangeServiceModal></VRequestChangeServiceModal>
            <VRequestChangeExecutorModal></VRequestChangeExecutorModal>
            <VRequestChangePriorityModal></VRequestChangePriorityModal>
            <VRequestSendToBitrix24Modal></VRequestSendToBitrix24Modal>
            <VRequestSolutionModal></VRequestSolutionModal>
            <VExportProgressModal></VExportProgressModal>
        </VAppLayout>

        <!-- While vuex is loading, we show the stub -->
        <div class="loading-app loading-app--center" v-else>
            <template v-if="isWinter && isSnowEnabled">
                <VChristmasTreeLoader></VChristmasTreeLoader>
            </template>

            <template v-else>
                <VSpinner></VSpinner>
            </template>

            <!-- Needs for the auth routes -->
            <template v-if="isSignInPage">
                <RouterView></RouterView>
            </template>
        </div>

        <VRequestCreateModal></VRequestCreateModal>
        <VGlobalSearchModal></VGlobalSearchModal>
        <VRequestRatingModal></VRequestRatingModal>

        <!-- Экран фатальной ошибки. В корне layout, вне обеих веток выше: он обязан
             показываться и поверх работающего приложения, и вместо спиннера, когда
             справочники так и не загрузились. -->
        <VAppErrorOverlay></VAppErrorOverlay>
    </div>
</template>
<script>
// Components
import VImagePopup from './views/components/VImagePopup/VImagePopup.vue';
import VErrorModal from './views/modals/VErrorModal.vue';
import VRequestPauseModal from './views/modals/VRequestPauseModal.vue';
import VRequestChangeServiceModal from './views/modals/VRequestChangeServiceModal.vue';
import VRequestChangeExecutorModal from './views/modals/VRequestChangeExecutorModal.vue';
import VRequestChangePriorityModal from './views/modals/VRequestChangePriorityModal.vue';
import VRequestSendToBitrix24Modal from './views/modals/VRequestSendToBitrix24Modal.vue';
import VRequestSolutionModal from './views/modals/VRequestSolutionModal.vue';
import VExportProgressModal from './views/modals/VExportProgressModal.vue';
import VRequestCreateModal from './views/modals/VRequestCreateModal.vue';
import VGlobalSearchModal from './views/modals/VGlobalSearchModal.vue';
import VRequestRatingModal from './views/modals/VRequestRatingModal.vue';
import VConfirmModal from './views/modals/VConfirmModal.vue';
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';
import ModalNames from '@/js/enums/ModalNames';

import VSpinner from '@/views/components/VProgress/VSpinner.vue';
import VChristmasTreeLoader from '@/views/components/VProgress/VChristmasTreeLoader.vue';
import VAppLayout from './views/components/VApp';
import { isVuexBooted } from './store';
import { isWinterSeason } from '@/js/season';

import IEpage from './views/pages/IEPage/IEPage.vue';
import VAppErrorOverlay from '@/views/components/VAppErrorOverlay/VAppErrorOverlay.vue';

// Other
import Vue from 'vue';
import oidc from './vue-oidc-client';
import Component from 'vue-class-component';
import './scss/style.scss';

const widthMediaQuery = window.matchMedia('(max-width: 800px)');

@Component({
    components: {
        IEpage,
        VAppErrorOverlay,
        VSpinner,
        VChristmasTreeLoader,
        VAppLayout,
        VImagePopup,
        Splitpanes,
        Pane,

        VErrorModal,
        VRequestPauseModal,
        VRequestChangeServiceModal,
        VRequestChangeExecutorModal,
        VRequestChangePriorityModal,
        VRequestSendToBitrix24Modal,
        VRequestSolutionModal,
        VExportProgressModal,
        VRequestCreateModal,
        VGlobalSearchModal,
        VConfirmModal,
        VRequestRatingModal,
    },
})
class App extends Vue {
    isVuexBooted = false;
    menuPaneSize = 20;
    windowWidth = window.innerWidth;
    requestPaneSize = 45;
    // Ширина боковой панели в разделе «Оборудование» — своя, и запоминается отдельно.
    // Панель в обоих разделах одна и та же по механике, но не по содержанию: заявке
    // широкая панель нужна ради переписки, а слева от неё — одна колонка карточек;
    // в инвентаре слева таблица из девяти колонок, и 45% на ноутбуке не оставляли ей
    // читаемой ширины. Общее значение делало выбор в одном разделе настройкой другого.
    inventoryPaneSize = 36;
    isSnowEnabled = true;
    isAdaptive = widthMediaQuery.matches ? true : false;

    get isIE() {
        const userAgent = window.navigator.userAgent.toLowerCase();

        return /trident/gi.test(userAgent) || /msie/gi.test(userAgent);
    }

    get isSignInPage() {
        return this.$route.name === 'signinwin-sso';
    }

    get isWinter() {
        return isWinterSeason();
    }

    vuexBootedHandler() {
        this.isVuexBooted = true;

        // bootVuex() resolves (and this handler runs) from inside the router's own beforeEach
        // guard, before it calls next() — $route is still the stale pre-navigation placeholder
        // at this point, not the URL actually requested. onReady() waits for the initial
        // navigation (guards included) to fully resolve, so $route.query is the real one by
        // then — otherwise a reloaded "?createRequest=true&..." link never opened the modal.
        this.$router.onReady(() => {
            const showCreateRequestModal = Boolean(this.$route.query.createRequest) === true;
            if (showCreateRequestModal) {
                this.showRequestCreateModal();
            }
        });

        oidc.startSilentRenew();
    }

    /**
     * Раздел, которому принадлежит боковая панель сейчас.
     */
    get isInventoryRoute() {
        return String(this.$route.name ?? '').startsWith('inventory');
    }

    /**
     * Ширина боковой панели текущего раздела.
     */
    get sharedPaneSize() {
        return this.isInventoryRoute ? this.inventoryPaneSize : this.requestPaneSize;
    }

    get minMenuPaneSize() {
        return Math.max(10, (240 / this.windowWidth) * 100);
    }

    get effectiveMenuPaneSize() {
        return Math.max(this.menuPaneSize, this.minMenuPaneSize);
    }

    saveRequestPaneSize(event) {
        if (!event[1] || this.isAdaptive) {
            return;
        }

        if (this.isInventoryRoute) {
            this.inventoryPaneSize = event[1].size;
            localStorage.setItem('inventoryPaneSize', this.inventoryPaneSize);

            return;
        }

        this.requestPaneSize = event[1].size;
        localStorage.setItem('requestPaneSize', this.requestPaneSize);
    }

    saveMenuPaneSize(event) {
        this.menuPaneSize = event[0].size;
        localStorage.setItem('menuPaneSize', this.menuPaneSize);
    }

    async showRequestCreateModal() {
        const request = await this.$showModal(ModalNames.REQUEST_CREATE);

        this.$router.push({
            name: 'request',
            params: {
                method: 'outbox',
                id: request.id,
            },
        });
    }

    handleToggleSnow(isEnabled) {
        this.isSnowEnabled = isEnabled;
        localStorage.setItem('toggleSnow', this.isSnowEnabled);
    }

    adaptivePanes() {
        this.isAdaptive = widthMediaQuery.matches ? true : false;
    }

    updateWindowWidth() {
        this.windowWidth = window.innerWidth;
    }

    created() {
        this.isVuexBooted = isVuexBooted;
        this.$bus.on('Vuex::Booted', this.vuexBootedHandler);
        this.$bus.on('toggleSnow', this.handleToggleSnow);
        this.menuPaneSize = Number(localStorage.getItem('menuPaneSize')) || 20;
        this.requestPaneSize = Number(localStorage.getItem('requestPaneSize')) || 45;
        this.inventoryPaneSize = Number(localStorage.getItem('inventoryPaneSize')) || 36;
        this.isSnowEnabled = localStorage.getItem('toggleSnow') ? JSON.parse(localStorage.getItem('toggleSnow')) : true;
        widthMediaQuery.addEventListener('change', this.adaptivePanes);
        window.addEventListener('resize', this.updateWindowWidth);
    }

    destroyed() {
        widthMediaQuery.removeEventListener('change', this.adaptivePanes);
        window.removeEventListener('resize', this.updateWindowWidth);
    }
}

export default App;
</script>
<style lang="scss">
.loading-app {
    display: flex;
    width: 100%;
    height: 1px;
    min-height: 100%;

    &--center {
        align-items: center;
        justify-content: center;
    }
}
.page {
    display: flex;
    width: 100%;
    height: 100%;
    &__menu-pane {
        position: relative;
        box-shadow: 0 0 0.6875rem 0 rgba(0, 0, 0, 0.07);
    }
}
.splitpanes--vertical .splitpanes__pane {
    transition: none;
}

// Граница слоёв всего приложения (шкала — в scss/_variables.scss).
//
// Панель — самостоятельный контекст наложения, поэтому z-index внутри неё замкнут на
// неё же. Без этого любое число из глубины панели соревновалось со всем проектом: и
// закреплённая ячейка таблицы, и бегунок с унаследованной от плагина 1010 перекрывали
// тени соседних панелей. isolation, а не position/z-index — он создаёт контекст,
// ничего не меняя в раскладке и в том, относительно чего считаются absolute-потомки.
.splitpanes__pane {
    isolation: isolate;
}

// Боковые панели лежат над рабочей областью: их тени ложатся на список и форму.
// Специфичность (0,2,0) — на голом классе z-index проигрывал правилу выше.
//
// Только z-index. position здесь задавать нельзя: правило перебивало бы адаптивное
// `.page__menu-pane { position: absolute }` (0,1,0) ниже, и панели переставали
// складываться на телефоне, вставая рядом по половине экрана.
.splitpanes--vertical .page__menu-pane,
.splitpanes--vertical .request_pane {
    z-index: $layer-pane-side;
}
// z-index работает только на позиционированном элементе. У панели меню position свой,
// адаптив его меняет; панели заявки его задаём здесь и в адаптиве не трогаем.
.request_pane {
    position: relative;
}
.splitpanes__splitter {
    position: relative;
    padding: 0 8px;
    margin: 0 -8px;
    z-index: $layer-splitter;
    &:hover {
        &::after {
            background-color: rgba(135, 206, 250);
            transition: background-color 0.15s 0.5s;
        }
    }
    &::after {
        position: absolute;
        content: ' ';
        width: 4px;
        top: 0;
        bottom: 0;
    }
}
// Место под нижнюю панель навигации: она position: fixed и лежит поверх содержимого
// (см. NavMenu), поэтому высоту под неё резервирует оболочка, а не каждая страница.
//
// env(safe-area-inset-bottom) — про iPhone с индикатором Home: там нижние 34 точки экрана
// принадлежат системе, панель навигации поднимается над ними на свою же величину,
// и без этой добавки последняя строка любой страницы оказывалась бы под панелью.
// На устройствах без выреза значение равно нулю, и ничего не меняется.
.app-layout {
    .app-layout {
        padding-bottom: calc(3.85rem + env(safe-area-inset-bottom, 0px));
    }
}
@media (min-width: $media-md) {
    .app-layout {
        .app-layout {
            padding-bottom: 0;
        }
    }
}
@media (max-width: $media-md) {
    // В адаптиве панели лежат друг на друге во всю ширину экрана. Панель меню остаётся
    // выше рабочей области — иначе оверлей открытого меню и нижняя панель навигации
    // оказываются под списком.
    //
    // Кликам она при этом не мешает: занята в ней только нижняя панель и оверлей,
    // а вся остальная площадь пропускает нажатия к списку под собой. Обратно события
    // включает сам NavMenu — на тех блоках, которые действительно видны.
    .page__menu-pane {
        position: absolute;
        pointer-events: none;
    }
}
</style>
